import { getUMKFromLocal } from 'pUtils'

const DB_NAME = 'safebox_attachment_cache'
const STORE_NAME = 'attachments'
const DB_VERSION = 1
const CACHE_TTL_MS = 60 * 60 * 1000

// 파일당 캐시 상한 (설계 §5.3). 초과 파일은 **기록하지 않는다** — 캐시는 성능 최적화이므로
// 미기록이 기능 저하가 아니고, 호출부는 캐시 미스와 동일 경로를 탄다(getCachedFile 은 null 반환).
//
// 상한이 필요한 진짜 이유는 스토리지 잠식이 아니라 **힙**이다: setCachedFile 은
// `blob.arrayBuffer()` 로 파일 전체를 힙에 올리고 encryptBuffer 가 한 벌 더 복사하므로,
// 450MB 파일이면 900MB 이상을 잡는다 — 대용량 첨부 작업이 없애려는 그 OOM 과 같은 종류다.
// 그래서 검사는 반드시 arrayBuffer() **앞**에 둔다(뒤에 두면 이미 터진 뒤다).
// helper 의 첨부 상한 상수를 import 하지 않는 이유: attachmentCache 는 pUtils 만 의존해
// 순환 참조가 없는 상태이고, 캐시 정책은 첨부 상한과 독립적으로 조정될 값이다.
const MAX_CACHEABLE_BYTES = 50 * 1024 * 1024

async function deriveKey() {
  const umk = await getUMKFromLocal()
  if (!umk) throw new Error('UMK not available')
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(umk),
    { name: 'HKDF' },
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode('safebox-attachment-cache'),
      info: new Uint8Array(),
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

async function encryptBuffer(buffer) {
  const key = await deriveKey()
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, buffer)
  const result = new Uint8Array(12 + encrypted.byteLength)
  result.set(iv, 0)
  result.set(new Uint8Array(encrypted), 12)
  return result.buffer
}

async function decryptBuffer(buffer) {
  const key = await deriveKey()
  const data = new Uint8Array(buffer)
  const iv = data.slice(0, 12)
  const encrypted = data.slice(12)
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, encrypted)
}

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (e) => {
      const db = e.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' })
      }
    }
    req.onsuccess = (e) => resolve(e.target.result)
    req.onerror = () => reject(new Error('Failed to open attachment cache DB'))
  })
}

export async function getCachedFile(cacheKey) {
  try {
    const db = await openDB()
    const entry = await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).get(cacheKey)
      req.onsuccess = (e) => resolve(e.target.result)
      req.onerror = () => resolve(null)
    })
    if (!entry || Date.now() - entry.timestamp > CACHE_TTL_MS) return null
    const decrypted = await decryptBuffer(entry.buffer)
    return new Blob([decrypted], { type: entry.mimeType })
  } catch {
    return null
  }
}

export function isCacheableSize(blob) {
  // size 불명은 **허용**한다 — Blob.size 는 스펙상 항상 존재하므로 이 분기는 운영에서 도달하지
  // 않고(스텁 입력에서만 발생), 상한의 목적은 "크다고 알려진" 파일을 막는 것이다.
  // 미리보기 크기 게이트(isPreviewSizeAllowed)도 불명을 허용하므로 정책을 일치시킨다.
  if (!blob || typeof blob.size !== 'number' || !Number.isFinite(blob.size)) return true
  return blob.size <= MAX_CACHEABLE_BYTES
}

export async function setCachedFile(cacheKey, blob) {
  if (!isCacheableSize(blob)) {
    // 조용히 넘기지 않고 남긴다 — "캐시가 왜 안 먹나" 를 추적할 단서가 필요하다
    console.info('[attachmentCache] skip caching — over per-file limit', {
      size: blob?.size,
      limit: MAX_CACHEABLE_BYTES,
    })
    return undefined
  }
  try {
    const buffer = await blob.arrayBuffer()
    const encrypted = await encryptBuffer(buffer)
    const db = await openDB()
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).put({
        cacheKey,
        buffer: encrypted,
        mimeType: blob.type,
        timestamp: Date.now(),
      })
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
    })
  } catch {
    return undefined
  }
}

export async function clearAttachmentCache() {
  try {
    const db = await openDB()
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).clear()
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
    })
  } catch {
    return undefined
  }
}
